import axiosInstant from "../../api";
import {
  createBrandOrder,
  createBrandOrderWithProjects,
  orderProsSync,
  createBrandProject,
  editProject,
  updateBrandOrder,
  pricingPreview,
} from "../../api/endpoints";
import { OrderPricingBreakdownData } from "../../components/Pricing/OrderPricingBreakdown";
import { ProjectPricingBreakdown } from "../../components/Pricing/PricingBreakdown";

export interface OrderProjectValues {
  id?: string;
  project_name: string;
  project_id?: number | null;
  content_type: string;
  content_type_id?: number | null;
  channel: string;
  channel_id?: number | null;
  channel_ids?: number[];
  channel_objects?: any[];
  channel_name?: string;
  rmn_brand_id?: number | null;
  product_name: string;
  product_id?: number | null;
  product_ids?: number[];
  products?: Array<{ desireid: number; id: number; title: string; image?: string }>;
  product_image?: string | null;
  start_date?: Date | null;
  pricing_total?: string | null;
  // Internal-only manual price adjustment (signed dollars). null = unset.
  // May arrive from the API as a DECIMAL string; coerced to Number on save.
  production_adjustment?: number | string | null;
  // When true the price is locked ("actual"); false/undefined = estimated.
  price_locked?: boolean;
  // Client-side key of the expert selection process (brand_order_pro) this
  // project is assigned to. Resolved to a real id server-side.
  order_pro_key?: string | null;
  // Current casting status (edit mode) — used to preview/converge selection
  // stages when projects are merged onto one selection.
  status?: string | null;
  // Brief intake: index of the brief product this project was drafted for.
  // Cleared when the project is edited by hand, so re-matching on the intake
  // step only rewrites projects that still follow the brief.
  intake_product?: number | null;
}

// One "expert selection process" (brand_order_pro) in the order form. `key` is
// a stable client id; `id` is present only for processes that already exist
// (edit mode). New processes carry a `key` with no `id`.
export interface OrderProProcess {
  key: string;
  id?: number;
  name: string;
  /** True when the process is locked (its selection reached Pre-Production). */
  locked?: boolean;
}

export interface GroupedOption {
  groupLabel: string;
  options: { label: string; value: number }[];
}

// Selection processes are scoped by a project's whole product SET (matches the
// backend's productSetKey): sorted, deduped ids, order-insensitive. Two
// projects may share a selection only when their sets are equal. null = no
// products yet.
export const orderProjectProductSet = (p: OrderProjectValues): string | null => {
  const ids = p.product_ids?.length
    ? p.product_ids
    : p.product_id != null
      ? [p.product_id]
      : [];
  if (ids.length === 0) return null;
  return Array.from(new Set(ids.map(Number))).sort((a, b) => a - b).join(",");
};

export const formatContentTypeName = (name: string): string => {
  if (!name) return "";
  return name
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

export const toStringValue = (val: any): string => {
  if (val === undefined || val === null) return "";
  return typeof val === "string" ? val.trim() : String(val);
};

export const groupChannelsByCategory = (channels: any[]): GroupedOption[] => {
  if (!channels?.length) return [];

  const grouped: Record<string, { label: string; value: number }[]> = {};
  const uncategorized: { label: string; value: number }[] = [];

  channels.forEach((ch) => {
    const option = { label: ch.name, value: ch.id };
    const categoryName = ch.category?.name;

    if (categoryName) {
      grouped[categoryName] = grouped[categoryName] || [];
      grouped[categoryName].push(option);
    } else {
      uncategorized.push(option);
    }
  });

  const result = Object.entries(grouped)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([groupLabel, options]) => ({
      groupLabel,
      options: options.sort((a, b) => a.label.localeCompare(b.label)),
    }));

  if (uncategorized.length > 0) {
    result.push({
      groupLabel: "Other",
      options: uncategorized.sort((a, b) => a.label.localeCompare(b.label)),
    });
  }

  return result;
};

export const createOrder = async (
  brandId: number,
  orderName: string,
  sowId?: number | null,
  slackChannel?: string | null,
  createSlackChannel?: boolean,
  newSlackChannelName?: string,
  downloadPage?: boolean,
) => {
  const res = await axiosInstant.post(createBrandOrder(), {
    brand_id: brandId,
    name: orderName.trim(),
    sow_id: sowId || null,
    slack_channel: slackChannel || null,
    create_slack_channel: createSlackChannel ?? false,
    new_slack_channel_name: createSlackChannel
      ? newSlackChannelName || null
      : null,
    download_page: downloadPage ?? false,
  });
  return res.data;
};

export const updateOrder = async (
  orderId: number,
  brandId: number,
  orderName: string,
  sowId?: number | null,
  slackChannel?: string | null,
  createSlackChannel?: boolean,
  newSlackChannelName?: string,
  downloadPage?: boolean,
) => {
  await axiosInstant.patch(updateBrandOrder(orderId), {
    brand_id: brandId,
    name: orderName.trim(),
    sow_id: sowId || null,
    slack_channel: slackChannel || null,
    create_slack_channel: createSlackChannel ?? false,
    new_slack_channel_name: createSlackChannel
      ? newSlackChannelName || null
      : null,
    download_page: downloadPage ?? false,
  });
};

export const updateExistingProject = async (
  initialBrandId: number,
  project: OrderProjectValues,
  brandId: number,
  isRmnOrder = false,
  // True when the project's selection process is locked (at/past
  // Pre-Production): SOW-frozen fields are omitted from the payload entirely
  // so the save can never trip the backend freeze or unfreeze the price.
  processLocked = false,
  // skipProducts: leave products for a later save (the project is about to
  // change selection). applyProductsToSelection: the whole selection changes
  // products together — the API writes the set to every sibling at once.
  productOpts: { skipProducts?: boolean; applyProductsToSelection?: boolean } = {},
) => {
  if (!project.project_id) return;
  const actualBrandId =
    isRmnOrder && project.rmn_brand_id ? project.rmn_brand_id : brandId;
  const rmnBrandId = isRmnOrder ? brandId : project.rmn_brand_id ?? null;
  const payload: Record<string, unknown> = {
    project_name: project.project_name.trim(),
    title: project.project_name.trim(),
    rmn_brand_id: rmnBrandId,
    brand_id: actualBrandId,
    start_date: project.start_date ? new Date(project.start_date) : null,
    production_adjustment: project.production_adjustment ?? null,
  };
  // Channels are only KNOWN after enrichment (which always sets channel_ids
  // to a real array). Sending the empty-array fallback for an un-enriched
  // project would wipe the server's channel assignments on an ordinary save.
  if (project.channel_ids !== undefined) {
    payload.content_channel_ids = project.channel_ids;
  } else if (project.channel_id) {
    payload.content_channel_ids = [project.channel_id];
  }
  if (!processLocked) {
    // Only send product/content-type when we actually KNOW them — an
    // enrichment fetch that failed leaves them empty, and sending null would
    // wipe real values on the server. The backend already has them.
    if (!productOpts.skipProducts) {
      if (project.product_ids?.length) payload.desireids = project.product_ids;
      else if (project.product_id != null) payload.desireid = project.product_id;
      if (productOpts.applyProductsToSelection) payload.apply_products_to_selection = true;
    }
    if (project.content_type_id != null) payload.content_type_id = project.content_type_id;
    // Same known-only rule: never unfreeze a price because the flag wasn't
    // loaded.
    if (project.price_locked !== undefined) payload.price_locked = project.price_locked;
  }
  await axiosInstant.patch(editProject(initialBrandId, project.project_id), payload);
};

export const createNewProject = async (
  brandId: number,
  project: OrderProjectValues,
  orderId: number,
  isRmnOrder = false,
  slackChannel?: string | null,
) => {
  const actualBrandId =
    isRmnOrder && project.rmn_brand_id ? project.rmn_brand_id : brandId;
  const rmnBrandId = isRmnOrder ? brandId : project.rmn_brand_id ?? null;
  const productPayload = project.product_ids?.length
    ? { desireids: project.product_ids }
    : { desireid: project.product_id };
  const res = await axiosInstant.post(createBrandProject(actualBrandId), {
    brand_id: actualBrandId,
    project_name: project.project_name.trim(),
    title: project.project_name.trim(),
    ...productPayload,
    content_type_id: project.content_type_id ?? null,
    content_channel_ids: project.channel_ids ?? (project.channel_id ? [project.channel_id] : []),
    rmn_brand_id: rmnBrandId,
    order_id: orderId,
    can_ship_two: true,
    online_purchase: true,
    over_100_lbs: false,
    start_date: project.start_date ? new Date(project.start_date) : null,
    production_adjustment: project.production_adjustment ?? null,
    price_locked: project.price_locked ?? false,
    ...(slackChannel ? { slack_channel: slackChannel } : {}),
  });
  return res.data;
};

// Atomic create: order + selection processes + projects in one request
// (POST /brand-orders/with-projects). Replaces the legacy order-first-then-
// parallel-projects orchestration. Mirrors createNewProject's per-project
// brand/RMN/product shaping so the backend builds identical projects.
export interface OrderWithProjectsParams {
  brandId: number | null;
  orderName: string;
  sowId?: number | null;
  slackChannel?: string | null;
  createSlackChannel?: boolean;
  newSlackChannelName?: string;
  downloadPage?: boolean;
  isRmnOrder?: boolean;
  orderPros: { key: string; name?: string | null }[];
  projects: OrderProjectValues[];
}

// The POST /brand-orders/with-projects body. Brief intake sends the same body
// as its `order`, so both create paths carry identical fields.
export const buildOrderWithProjectsPayload = (params: OrderWithProjectsParams) => {
  const {
    brandId,
    orderName,
    sowId,
    slackChannel,
    createSlackChannel,
    newSlackChannelName,
    downloadPage,
    isRmnOrder = false,
    orderPros,
    projects,
  } = params;
  return {
    brand_id: brandId,
    name: orderName.trim(),
    sow_id: sowId || null,
    slack_channel: slackChannel || null,
    create_slack_channel: createSlackChannel ?? false,
    new_slack_channel_name: createSlackChannel ? newSlackChannelName || null : null,
    download_page: downloadPage ?? false,
    order_pros: orderPros.map((op) => ({ key: op.key, name: op.name || null })),
    projects: projects.map((p) => {
      const actualBrandId =
        isRmnOrder && p.rmn_brand_id ? p.rmn_brand_id : brandId;
      const rmnBrandId = isRmnOrder ? brandId : p.rmn_brand_id ?? null;
      const productPayload = p.product_ids?.length
        ? { desireids: p.product_ids }
        : { desireid: p.product_id };
      return {
        brand_id: actualBrandId,
        project_name: p.project_name.trim(),
        title: p.project_name.trim(),
        ...productPayload,
        content_type_id: p.content_type_id ?? null,
        content_channel_ids:
          p.channel_ids ?? (p.channel_id ? [p.channel_id] : []),
        rmn_brand_id: rmnBrandId,
        can_ship_two: true,
        online_purchase: true,
        over_100_lbs: false,
        start_date: p.start_date ? new Date(p.start_date) : null,
        production_adjustment: p.production_adjustment ?? null,
        price_locked: p.price_locked ?? false,
        order_pro_key: p.order_pro_key ?? (orderPros[0]?.key ?? null),
      };
    }),
  };
};

export const createOrderWithProjects = async (params: OrderWithProjectsParams) => {
  const res = await axiosInstant.post(
    createBrandOrderWithProjects(),
    buildOrderWithProjectsPayload(params)
  );
  return res.data;
};

// Internal-only declarative sync of an order's selection processes + project
// assignments (PUT /brand-orders/:id/order-pros).
export const syncOrderPros = async (
  orderId: number,
  orderPros: { id?: number; key?: string; name?: string | null }[],
  projectAssignments: {
    project_id: number;
    order_pro_id?: number;
    order_pro_key?: string;
    // Products the project saves right after this sync (guard input).
    desireids?: number[];
  }[],
) => {
  const res = await axiosInstant.put(orderProsSync(orderId), {
    order_pros: orderPros,
    project_assignments: projectAssignments,
  });
  return res.data;
};

export interface PreviewProjectEstimate {
  key: number;
  total: number;
  breakdown: ProjectPricingBreakdown;
}

export interface PricingPreviewResponse {
  per_project_estimates: PreviewProjectEstimate[];
  order: { total: number; breakdown: OrderPricingBreakdownData };
}

/**
 * Price an unsaved draft order via the backend preview endpoint (runs the real
 * pricing formula, persists nothing). Each project is keyed by its index so the
 * caller can map estimates back to form rows.
 */
export const previewPricing = async (
  projects: OrderProjectValues[],
  brandId?: number,
  sowId?: number | null,
): Promise<PricingPreviewResponse> => {
  const payload = {
    brand_id: brandId ?? null,
    // When set and the SOW has a package, the preview response carries
    // order.breakdown.package (flat price + projected SKU consumption).
    sow_id: sowId ?? null,
    projects: projects.map((p, i) => ({
      key: i,
      content_type_id: p.content_type_id ?? null,
      product_ids: p.product_ids?.length
        ? p.product_ids
        : p.product_id
          ? [p.product_id]
          : [],
      channel_ids: p.channel_ids?.length
        ? p.channel_ids
        : p.channel_id
          ? [p.channel_id]
          : [],
      license_key: null,
      production_adjustment:
        p.production_adjustment == null || p.production_adjustment === ""
          ? null
          : Number(p.production_adjustment),
      confirmed_expert_rates: [],
      option_selections: [],
    })),
  };
  const res = await axiosInstant.post(pricingPreview, payload);
  return res.data;
};

export const unlinkDeletedProjects = async (
  initialBrandId: number,
  deletedIds: number[],
) => {
  for (const id of deletedIds) {
    await axiosInstant.patch(editProject(initialBrandId, id), {
      order_id: null,
    });
  }
};
